import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'localization';
import type { TFunction } from 'i18next';
import type { RootState } from '@center-pwa/store/store';
import CustomTabs, { type CustomTabsHandle } from '../../atoms/CustomTabs/CustomTabs';
import SpherePropertiesMolecule from '../../molecules/SphereProperties/SphereProperties';
import SectionGroupBodyMolecule from '../../molecules/SphereProperties/SectionGroupBodyMolecule';
import CollapsibleSectionAtom from '../../atoms/CollapsibleSection/CollapsibleSectionAtom';
import EmptyStateAtom from '../../atoms/EmptyState/EmptyStateAtom';
import SkeletonAtom from '../../atoms/Skeleton/SkeletonAtom';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import { HEADER_RENDERED_PROPERTY_NAMES, resolveSectionLayout, type SectionLayout, type SectionLayoutGroup } from '../../../utils/resolveSectionLayout';
import { computeWriteBlockedProps } from '../../../utils/computeWriteBlockedProps';
import { SPHERE_SECTION_SLOTS, type SphereSectionSlot } from './sphereSectionSlots';
import { useSectionSlotDataSources } from './useSectionSlotDataSources';
import AtlasResponsiblePerspectives from '../../atoms/atlas-responsible-perspectives/atlas-responsible-perspectives';
import AtlasPerspectiveDetails from '../../molecules/Atlas/atlas-perspectives-details/atlas-perspectives-details';
import AtlasPerspectiveDetailPanel from '../../atoms/atlas-perspectives-details-panel/atlas-perspectives-details-panel';
import PeriodSelectorOrganism, { ExistingRelationship } from '../PeriodSelector/PeriodSelectorOrganism';
import UnifiedPropertyRendererMolecule from '../../molecules/UnifiedPropertyRenderer/UnifiedPropertyRendererMolecule';
import { resolveRenderer } from '../../../utils/resolveRenderer';
import { isMultiFieldLegacy, isMultiFieldChangeMapper, fanOutMultiFieldChange, setIfChanged } from '../../../utils/legacyMultiFieldFanout';
import { useMediaQuery } from '../../../hooks/useMediaQuery';
import type { AssignedPerspectives, Sphere, UpdateAck } from 'types/global';
import type { Schema } from 'types/schema';
import { Formik, Form, useFormikContext } from 'formik';
import { filterHiddenPropertiesExcluding, getTranslation } from '@center-platform/spheres/identity';
import { getRelationships, getSphereById, sphereIdentityFromSchema } from '@center-platform/spheres/sphere';
import { Toast } from 'primereact/toast';
import { Dialog } from 'primereact/dialog';
import ConfirmDialogAtom, {
  confirmDialogAtom,
} from '../../atoms/ConfirmDialog/ConfirmDialogAtom';
import PerspectiveAssignmentsPanel from './PerspectiveAssignmentsPanel';
import perspectiveToSchemaProperty from './PerspectiveTransform';
import { editorValidationSchema } from './editorValidationSchema';
import { requiredPerspectiveFields, retypeFormProperties } from './retypeFormProperties';
import { dockedPropertyTabId, sectionTabId, useEditorTabIndex } from './useEditorTabIndex';
import StatusFactorsTab from './StatusFactorsTab';
import SphereChannelsTab from './SphereChannelsTab';
import SphereApproachesTab from './SphereApproachesTab';
import SphereArcsTab from './SphereArcsTab';
import MeasureMembersListMolecule from '../../molecules/MeasureMembersListMolecule/MeasureMembersListMolecule';
import ChipAtom from '../../atoms/Chip/ChipAtom';
import { PermissionList } from 'types/permissions';
import type { RenderMode } from 'types/aspect';
import './SphereEditor.scss';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import { useSchemaType } from '../../../hooks/useSchemaType';
import FormikDebugger from './FormikDebugger';
import PurposeSection from './PurposeSection';

/** The list a sphere has until the server answers for it: nothing granted. */
const NO_PERMISSIONS: PermissionList = { permissions: {} };

interface SphereEditorProps {
  sphere: Sphere;
  onUpdate: (updatedSphere: Sphere) => Promise<UpdateAck>;
  editable?: boolean;
  parentSphereType?: string;
  onClose?: () => void;
  isEditing?: boolean;
  isSphereSubmitting?: boolean;
  showTabs?: boolean;
  viewMode?: boolean;
  organizationId?: string;
  fetchPermissionList: (sphereId: string, nodeLabel: string, permissions?: string[]) => Promise<PermissionList | null>;
  initialTab?: string; // Tab ID to open initially (e.g., 'status-factors-tab')
  initialArcId?: string; // Arc ID to pre-select in the Arcs tab
  onStatusFactorsChanged?: () => void;
  /**
   * Called with the sphere's id when a write made outside the form (the header avatar, the Identity
   * row) has changed the stored sphere. The editor stays open; the views behind it refresh.
   */
  onSphereChanged?: (sphereId: string) => void;
  /** Section slots, keyed by section system_name; defaults to the registry. */
  sectionSlots?: Readonly<Record<string, SphereSectionSlot>>;
}

interface SpherePropertiesEditorProps {
  sphere: Sphere;
  schemaType: any;
  editable: boolean;
  parentSphereType?: string;
  isEditing: boolean;
  perspectiveAssignments: any[];
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
  onDockedPropertyEdit?: (propertyName: string) => void;
  organizationId?: string;
  onNavigateToArc?: (arcId: string) => void;

  // Parent-scope captures hoisted to props so this component can live at
  // module scope. Defining it inside the parent's render body produced a new
  // function reference every render, which made React unmount + remount this
  // entire subtree (and everything below it — SphereProperties, AttachmentsGrid,
  // SphereChatPanel, RelationshipGrid). Hoisting + React.memo lets siblings
  // skip unchanged renders entirely.
  allRelationships: any[];
  setAllRelationships: React.Dispatch<React.SetStateAction<any[]>>;
  relationshipsLoading: boolean;
  relationshipsError: string | null;
  effectiveViewMode: boolean;
  effectiveOrganizationId: string | null;
  graphClient: any;
  selectedNodePermissionList: PermissionList;
  allRegularProperties: any[];
  handleShowPeriodSelector: () => void;
  writeBlockedProps?: Set<string>;
  sectionSlots?: Readonly<Record<string, SphereSectionSlot>>;
  onTypeChange: (type: Schema) => void;
  savedRevision?: number;
  onOpenSectionTab?: (sectionId: string) => void;
  onSphereChanged?: (sphereId: string) => void;
}

const SpherePropertiesEditor: React.FC<SpherePropertiesEditorProps> = React.memo(
  ({
    sphere,
    schemaType,
    editable,
    parentSphereType,
    isEditing,
    perspectiveAssignments,
    onDockedPropertyEdit,
    organizationId,
    onNavigateToArc,
    allRelationships,
    setAllRelationships,
    relationshipsLoading,
    relationshipsError,
    effectiveViewMode,
    effectiveOrganizationId,
    graphClient,
    selectedNodePermissionList,
    allRegularProperties,
    handleShowPeriodSelector,
    writeBlockedProps,
    sectionSlots,
    onTypeChange,
    savedRevision,
    onOpenSectionTab,
    onSphereChanged,
  }) => {
    const { t } = useTranslation();
    const isMobile = useMediaQuery('(max-width: 768px)');

    const [isPerspectiveExpanded, setIsPerspectiveExpanded] = useState(!isMobile);

    const mainScrollRef = useRef<any>(null);
    const perspectiveScrollRef = useRef<any>(null);
    const prevSphereIdRef = useRef<string | undefined>(undefined);

    const resetScrollPosition = useCallback(() => {
      if (mainScrollRef.current) {
        mainScrollRef.current.scrollTop = 0;
        mainScrollRef.current.scrollLeft = 0;
      }
      if (perspectiveScrollRef.current) {
        perspectiveScrollRef.current.scrollTop = 0;
        perspectiveScrollRef.current.scrollLeft = 0;
      }
      if (isMobile) {
        const dialogContent = document.querySelector('.p-dialog-content');
        if (dialogContent) (dialogContent as HTMLElement).scrollTop = 0;
        const sphereEditorContainer = document.querySelector('.sphere-editor-container');
        if (sphereEditorContainer) (sphereEditorContainer as HTMLElement).scrollTop = 0;
      }
    }, [isMobile]);

    useEffect(() => {
      const isInitialMount = prevSphereIdRef.current === undefined;
      const hasSphereChanged = prevSphereIdRef.current !== sphere.id;
      if (isInitialMount || hasSphereChanged) {
        const timer = setTimeout(() => resetScrollPosition(), 100);
        return () => clearTimeout(timer);
      }
      prevSphereIdRef.current = sphere.id;
    }, [sphere.id, resetScrollPosition]);

    const periodRelationships = useMemo(
      () =>
        allRelationships.filter(
          (rel: any) => rel.relationshipType === 'applies_to_period' && rel.active,
        ),
      [allRelationships],
    );

    const handleRelationshipsRefresh = useCallback(async () => {
      if (!graphClient || !sphere.id || !effectiveOrganizationId) return;
      try {
        const result = await getRelationships(graphClient, {
          sphereId: sphere.id,
        });
        if (result.success && result.relationships) {
          const relationshipsWithSpheres = await Promise.all(
            result.relationships.map(async (rel: any) => {
              try {
                const sphereResult = await getSphereById(graphClient, rel.targetId, true);
                if (sphereResult.success) {
                  return {
                    ...rel,
                    targetSphere: sphereResult.data.sphere || sphereResult.data,
                  };
                }
              } catch (error) {
                console.warn(`Failed to fetch target sphere ${rel.targetId}:`, error);
              }
              return rel;
            }),
          );
          setAllRelationships(relationshipsWithSpheres);
        }
      } catch (error) {
        console.error('SphereEditor — Failed to refresh relationships:', error);
      }
    }, [sphere.id, effectiveOrganizationId, graphClient, setAllRelationships]);

    return (
      <div className="sphere-editor-content">
        <div
          ref={mainScrollRef}
          className="sphere-editor-scroll-panel main-scroll-simple"
          style={{ height: '100%', overflowY: 'auto', overflowX: 'hidden' }}
        >
          <div className="sphere-editor-tabs">
            <SpherePropertiesMolecule
              sphere={sphere}
              schema={{
                ...schemaType,
                properties: allRegularProperties,
              }}
              editable={effectiveViewMode ? false : editable}
              parentSphereType={parentSphereType}
              onDockedPropertyEdit={onDockedPropertyEdit}
              organizationId={organizationId}
              relationships={allRelationships}
              periodRelationships={periodRelationships}
              relationshipsLoading={relationshipsLoading}
              relationshipsError={relationshipsError}
              onRelationshipsChange={handleRelationshipsRefresh}
              onShowPeriodSelector={handleShowPeriodSelector}
              permissionList={selectedNodePermissionList}
              onNavigateToArc={onNavigateToArc}
              writeBlockedProps={writeBlockedProps}
              sectionSlots={sectionSlots}
              onTypeChange={onTypeChange}
              savedRevision={savedRevision}
              onOpenSectionTab={onOpenSectionTab}
              onSphereChanged={onSphereChanged}
            />
          </div>
        </div>

        {isMobile ? (
          <div className="perspective-assignments-container mobile-collapsible-perspective">
            <button
              type="button"
              className="collapsible-header-perspective"
              onClick={() => setIsPerspectiveExpanded(!isPerspectiveExpanded)}
            >
              <span className="collapsible-title">
                {t('sphere.editor.perspectiveAssignments')}
              </span>
              <i
                className={`pi ${
                  isPerspectiveExpanded ? 'pi-chevron-down' : 'pi-chevron-right'
                } collapsible-icon`}
              />
            </button>
            {isPerspectiveExpanded && (
              <div className="collapsible-content-perspective">
                <PerspectiveAssignmentsPanel
                  sphere={sphere}
                  perspectives={schemaType?.perspectives ?? []}
                  perspectiveAssignments={perspectiveAssignments}
                  isEditing={effectiveViewMode ? false : isEditing}
                />
              </div>
            )}
          </div>
        ) : (
          <div className="perspective-assignments-container">
            <div
              ref={perspectiveScrollRef}
              className="sphere-editor-scroll-panel perspective-scroll-simple"
              style={{ height: '400px', overflowY: 'auto', overflowX: 'hidden' }}
            >
              <PerspectiveAssignmentsPanel
                sphere={sphere}
                perspectives={schemaType?.perspectives ?? []}
                perspectiveAssignments={perspectiveAssignments}
                isEditing={effectiveViewMode ? false : isEditing}
              />
            </div>
          </div>
        )}
      </div>
    );
  },
);
SpherePropertiesEditor.displayName = 'SpherePropertiesEditor';

/**
 * The seven tab builders below are independent of Formik's per-field state, so they are called
 * once per `SphereEditorOrganism` render rather than inside the Formik render-prop (previously
 * recomputed on every keystroke there). Each returns a `CustomTabs` tab or `null`; extracting
 * them keeps each tab's own branching out of the caller that assembles the tab list.
 */

interface BuildArcsTabParams {
  sphere: Sphere;
  effectiveViewMode: boolean;
  effectiveOrganizationId?: string;
  apiClients: any;
  canEdit: boolean;
  onStatusFactorsChanged?: () => void;
  pendingArcIdRef: React.MutableRefObject<string | null>;
  initialArcId?: string;
  t: TFunction;
}

function buildArcsTab({
  sphere,
  effectiveViewMode,
  effectiveOrganizationId,
  apiClients,
  canEdit,
  onStatusFactorsChanged,
  pendingArcIdRef,
  initialArcId,
  t,
}: BuildArcsTabParams) {
  if (!sphere.id || effectiveViewMode || !sphere.arcs || sphere.arcs.length === 0) return null;
  return {
    id: 'arcs-tab',
    header: t('sphereArcs.tab.label', { count: sphere.arcs.length }),
    content: (
      <SphereArcsTab
        sphere={sphere}
        organizationId={effectiveOrganizationId || ''}
        apiClients={apiClients}
        hasEditPermission={canEdit}
        onSphereRefresh={onStatusFactorsChanged}
        initialArcId={pendingArcIdRef.current || initialArcId}
      />
    ),
  };
}

interface BuildPurposeTabParams {
  sphere: Sphere;
  graphClient: any;
  canEdit: boolean;
  effectiveViewMode: boolean;
  effectiveOrganizationId?: string;
  t: TFunction;
}

/** Read-only when the server or the view mode says so, so it shows in view mode too. */
function buildPurposeTab({ sphere, graphClient, canEdit, effectiveViewMode, effectiveOrganizationId, t }: BuildPurposeTabParams) {
  if (!sphere.id) return null;
  return {
    id: 'purpose-tab',
    header: t('purpose.field.section'),
    content: (
      <PurposeSection
        graphClient={graphClient}
        sphereId={sphere.id}
        sphereName={sphere.properties?.name}
        hasEditPermission={canEdit}
        viewMode={effectiveViewMode}
        organizationId={effectiveOrganizationId}
      />
    ),
  };
}

interface BuildPerspectiveTabParams {
  sphere: Sphere;
  effectiveViewMode: boolean;
  t: TFunction;
  columns: any;
  responsiblePerspectiveOptions: any;
  handleResponsiblePerspectiveChange: (e: { value: any[] }) => void;
  setSelectedResponsiblePerspective: React.Dispatch<React.SetStateAction<any[]>>;
  updatedSelectedOptions: any[];
  selectedNodePermissionList: PermissionList;
  selectedPerspectiveSystemName: string | null;
  setSelectedPerspectiveSystemName: React.Dispatch<React.SetStateAction<string | null>>;
  onParticipantSelect: (value: any) => void;
  handleAssignPerspective: (...args: any[]) => any;
  selectedParticipant: any;
  onPerspectiveSelect: (value: any) => void;
  selectedPerspective: any;
}

function buildPerspectiveTab({
  sphere,
  effectiveViewMode,
  t,
  columns,
  responsiblePerspectiveOptions,
  handleResponsiblePerspectiveChange,
  setSelectedResponsiblePerspective,
  updatedSelectedOptions,
  selectedNodePermissionList,
  selectedPerspectiveSystemName,
  setSelectedPerspectiveSystemName,
  onParticipantSelect,
  handleAssignPerspective,
  selectedParticipant,
  onPerspectiveSelect,
  selectedPerspective,
}: BuildPerspectiveTabParams) {
  if (!sphere.id || effectiveViewMode) return null;
  return {
    id: 'perspectives-tab',
    header: t('sphere.editor.perspectiveAssignments'),
    content: (
      <div className="perspective-assignments-tab-content">
        <div style={{ flex: '1' }}>
          <AtlasPerspectiveDetails
            columns={columns}
            sphereId={sphere.id}
            sphere={sphere}
            options={responsiblePerspectiveOptions}
            onChange={handleResponsiblePerspectiveChange}
            setSelectedResponsiblePerspective={setSelectedResponsiblePerspective}
            value={updatedSelectedOptions}
            permissionList={selectedNodePermissionList}
            selectedPerspectiveSystemName={selectedPerspectiveSystemName!}
            setSelectedPerspectiveSystemName={setSelectedPerspectiveSystemName}
            onParticipantSelect={onParticipantSelect}
          />
        </div>
        <div style={{ flex: '1' }}>
          <AtlasResponsiblePerspectives
            value={updatedSelectedOptions}
            options={responsiblePerspectiveOptions}
            onChange={handleResponsiblePerspectiveChange}
            handleAssignPerspective={handleAssignPerspective}
            className="perspectives-responsible-dropdown"
            permissionList={selectedNodePermissionList}
            onPerspectiveSelect={onPerspectiveSelect}
            setSelectedPerspectiveSystemName={setSelectedPerspectiveSystemName}
            selectedParticipant={selectedParticipant}
          />
        </div>
        <div style={{ flex: '1' }}>
          <AtlasPerspectiveDetailPanel perspective={selectedPerspective} />
        </div>
      </div>
    ),
  };
}

interface BuildStatusFactorsTabParams {
  sphere: Sphere;
  effectiveViewMode: boolean;
  isMobile: boolean;
  t: TFunction;
  apiClients: any;
  effectiveOrganizationId?: string;
  canEdit: boolean;
  setHasUnsavedFactorChanges: (value: boolean) => void;
  onStatusFactorsChanged?: () => void;
}

function buildStatusFactorsTab({
  sphere,
  effectiveViewMode,
  isMobile,
  t,
  apiClients,
  effectiveOrganizationId,
  canEdit,
  setHasUnsavedFactorChanges,
  onStatusFactorsChanged,
}: BuildStatusFactorsTabParams) {
  if (!sphere.id || effectiveViewMode || isMobile) return null;
  return {
    id: 'status-factors-tab',
    header: t('statusFactors.title', 'Status Factors'),
    content: (
      <StatusFactorsTab
        sphere={sphere}
        organizationId={effectiveOrganizationId || ''}
        apiClients={apiClients}
        hasEditPermission={canEdit}
        onUnsavedChangesChange={setHasUnsavedFactorChanges}
        onFactorAdded={onStatusFactorsChanged}
        onFactorUpdated={onStatusFactorsChanged}
        onFactorRemoved={onStatusFactorsChanged}
      />
    ),
  };
}

interface BuildSimpleTabParams {
  sphere: Sphere;
  effectiveViewMode: boolean;
  t: TFunction;
  apiClients: any;
  effectiveOrganizationId?: string;
  canEdit: boolean;
}

function buildApproachesTab({ sphere, effectiveViewMode, t, apiClients, effectiveOrganizationId, canEdit }: BuildSimpleTabParams) {
  if (!sphere.id || effectiveViewMode) return null;
  return {
    id: 'approaches-tab',
    header: t('sphereApproaches.tab.label'),
    content: <SphereApproachesTab sphere={sphere} organizationId={effectiveOrganizationId || ''} apiClients={apiClients} hasEditPermission={canEdit} />,
  };
}

function buildChannelsTab({ sphere, effectiveViewMode, t, apiClients, effectiveOrganizationId, canEdit }: BuildSimpleTabParams) {
  if (!sphere.id || effectiveViewMode) return null;
  return {
    id: 'channels-tab',
    header: t('sphereChannels.tab.label'),
    content: <SphereChannelsTab sphere={sphere} organizationId={effectiveOrganizationId || ''} apiClients={apiClients} hasEditPermission={canEdit} />,
  };
}

interface BuildMeasureAggregateTabParams {
  sphere: Sphere;
  canEdit: boolean;
  effectiveViewMode: boolean;
  t: TFunction;
}

/**
 * Shown in view mode too: GET measure-members needs only `view`. Mutation affordances are gated
 * on `!effectiveViewMode` (not raw `canEdit`) so a deliberate "View" open (viewMode=true via the
 * tree row menu, available to editors too) suppresses weight/unlink controls the same way every
 * sibling tab does by hiding entirely — otherwise an editor who explicitly chose "View" would
 * still see live edit affordances here.
 */
function buildMeasureAggregateTab({ sphere, canEdit, effectiveViewMode, t }: BuildMeasureAggregateTabParams) {
  if (!sphere.id || sphere.properties?.measureRole !== 'aggregate') return null;
  return {
    id: 'measure-members-tab',
    header: t('measureMembers.tab.label'),
    content: <MeasureMembersListMolecule aggregateSphereId={sphere.id} canEdit={canEdit && !effectiveViewMode} />,
  };
}

interface FirstInvalidTabActivatorProps {
  propertyOrder: string[];
  /** Tab index for a property edited outside the Properties tab (docked section or legacy docked tab). */
  tabIndexByProperty: Record<string, number>;
  onActivate: (tabIndex: number) => void;
}

/**
 * After a failed submit, activates the tab holding the first invalid property (in schema order) so
 * a validation error is never hidden behind another tab. Renders nothing.
 */
const FirstInvalidTabActivator: React.FC<FirstInvalidTabActivatorProps> = ({ propertyOrder, tabIndexByProperty, onActivate }) => {
  const { errors, submitCount, isValidating, isSubmitting } = useFormikContext<{ properties?: Record<string, unknown> }>();
  const seenSubmitCount = useRef(0);
  const pendingSubmit = useRef(false);

  useEffect(() => {
    if (submitCount !== seenSubmitCount.current) {
      seenSubmitCount.current = submitCount;
      pendingSubmit.current = true;
    }
    if (!pendingSubmit.current || isValidating) return;
    if (isSubmitting) {
      pendingSubmit.current = false;
      return;
    }
    const propertyErrors = errors.properties as Record<string, unknown> | undefined;
    const firstInvalid = propertyOrder.find((name) => !!propertyErrors?.[name]);
    // Validation has settled for this submit: clear it even when only non-property fields failed, so
    // a later edit's validation never jumps tabs on its own.
    pendingSubmit.current = false;
    if (firstInvalid === undefined) return;
    onActivate(tabIndexByProperty[firstInvalid] ?? 0);
  }, [submitCount, errors, isValidating, isSubmitting, propertyOrder, tabIndexByProperty, onActivate]);

  return null;
};

/**
 * Once the schema of a type picked for a new sphere has loaded, moves the form's values onto it (see
 * `retypeFormProperties`): defaults and required-perspective fields follow the new type, and nothing
 * the person entered is lost. Renders nothing.
 */
const RetypeFormValues: React.FC<{ schema: Schema | null }> = ({ schema }) => {
  const { values, setValues } = useFormikContext<{ properties: Record<string, unknown>; assignedPerspectives?: AssignedPerspectives }>();
  // The type the form's values currently follow.
  const followedSchema = useRef<Schema | null>(null);

  useEffect(() => {
    if (!schema) return;
    const from = followedSchema.current;
    followedSchema.current = schema;
    if (!from || from.system_name === schema.system_name) return;
    void setValues({ ...values, properties: retypeFormProperties(values.properties, from, schema, values.assignedPerspectives) });
  }, [schema, values, setValues]);

  return null;
};

const SphereEditorOrganism: React.FC<SphereEditorProps> = ({
  sphere,
  onUpdate,
  editable = true,
  parentSphereType,
  onClose,
  isEditing = true,
  isSphereSubmitting = false,
  showTabs = true,
  viewMode = false,
  organizationId,
  fetchPermissionList,
  initialTab,
  initialArcId,
  onStatusFactorsChanged,
  onSphereChanged,
  sectionSlots = SPHERE_SECTION_SLOTS,
}) => {
  const { t, i18n } = useTranslation();
  const apiClients = useApiClientsOrNull();

  // A new sphere's type can be changed in the Properties header. The pick is held here so the section
  // layout, docked tabs, slots and validation all follow it, and it belongs to the sphere it was made for.
  const [typeChoice, setTypeChoice] = useState<{ sphereId: string; type: Schema } | null>(null);
  const chosenType = typeChoice?.sphereId === sphere.id ? (typeChoice?.type ?? null) : null;
  const handleTypeChange = useCallback((type: Schema) => setTypeChoice({ sphereId: sphere.id, type }), [sphere.id]);

  const ownTypeName = sphere.properties.type ?? null;
  const typeName = chosenType?.system_name ?? ownTypeName;
  // The sphere's own type seeds the form; the chosen type, once there is one, drives everything else.
  const { schema: ownSchema } = useSchemaType(ownTypeName);
  const { schema: schemaType, status: schemaTypeStatus, retry: retrySchemaType } = useSchemaType(typeName);

  // The sphere as the chosen type: its label and glyph show in the header and are saved with it.
  const editedSphere = useMemo<Sphere>(
    () =>
      chosenType
        ? { ...sphere, ...sphereIdentityFromSchema(chosenType), properties: { ...sphere.properties, type: chosenType.system_name } }
        : sphere,
    [sphere, chosenType],
  );

  // Check edit permissions and update the permission list state
  // Edit permission has three states: until the server answers for THIS sphere it is unknown, and
  // unknown is not allowed. A result belongs to the sphere it was fetched for, so switching spheres
  // returns to unknown instead of reusing the previous sphere's answer. The permission list is keyed
  // the same way: until this sphere's list arrives it grants nothing.
  const [permissionAnswer, setPermissionAnswer] = useState<{ sphereId: string; allowed: boolean; list: PermissionList } | null>(null);
  const answerForThisSphere = permissionAnswer && sphere.id && permissionAnswer.sphereId === sphere.id ? permissionAnswer : null;
  const canEditState: 'unknown' | 'allowed' | 'denied' = (() => {
    if (!sphere.id || !isEditing) return 'allowed';
    if (!answerForThisSphere) return 'unknown';
    return answerForThisSphere.allowed ? 'allowed' : 'denied';
  })();
  const canEdit = canEditState === 'allowed';
  const selectedNodePermissionList = answerForThisSphere ? answerForThisSphere.list : NO_PERMISSIONS;
  const [selectedPerspective, setSelectedPerspective] = useState(null);
  const [selectedParticipant, setSelectedParticipant] = useState<any>(null);
  const [selectedPerspectiveSystemName, setSelectedPerspectiveSystemName] = useState<string | null>(null);

  // Relationships state - managed centrally to avoid duplicate API calls
  const [allRelationships, setAllRelationships] = useState<any[]>([]);
  const [relationshipsLoading, setRelationshipsLoading] = useState(false);
  const [relationshipsError, setRelationshipsError] = useState<string | null>(null);

  // Period selector state - for tab takeover functionality
  const [showPeriodSelector, setShowPeriodSelector] = useState(false);
  const [periodSelectorValue, setPeriodSelectorValue] = useState<Sphere[]>([]);
  const [periodSelectorExistingRels, setPeriodSelectorExistingRels] = useState<ExistingRelationship[]>([]);

  // Track unsaved changes in StatusFactorsTab for close warning
  const [hasUnsavedFactorChanges, setHasUnsavedFactorChanges] = useState(false);

  // Revision counter to trigger expected aspects refresh after save
  const [savedRevision, setSavedRevision] = useState<number>(0);

  // `version` bumps when a slot's fetched data changes; `appliesTo`/`ownsProperty` read those stores
  // synchronously, so it belongs in the section layout memo deps.
  const { version: sectionSlotVersion, invalidate: invalidateSectionSlots } = useSectionSlotDataSources(sectionSlots, sphere.id);

  // Store initial perspective assignments for comparison (used to detect unsaved changes)
  const initialPerspectiveAssignmentsRef = useRef(sphere.assignedPerspectives || []);

  // Guard to prevent confirmation dialog from opening multiple times
  const isConfirmDialogOpenRef = useRef(false);

  // Active tab index for controlled CustomTabs - allows switching tabs programmatically
  const [activeTabIndex, setActiveTabIndex] = useState<number>(0);
  const initialTabAppliedRef = useRef(false);

  // Pending arc navigation — set from Properties tab, applied inside Formik render where tab indices are known
  const [pendingArcId, setPendingArcId] = useState<string | null>(null);
  const pendingArcIdRef = useRef<string | null>(null);

  // Detect mobile viewport
  const isMobile = useMediaQuery('(max-width: 768px)');

  // Get organizationId from Redux store if not provided as prop
  const storeOrganizationId = useSelector((state: RootState) => state.nodes.organizationId);

  // Extract graphClient - may be null if apiClients is null
  const graphClient = apiClients?.graphClient ?? null;
  const effectiveOrganizationId = organizationId || storeOrganizationId || sphere?.properties?.sphereId;

  const tabsRef = useRef<CustomTabsHandle>(null);
  // Tab whose header takes focus once it is active: the control that opened it lived in the panel
  // the switch unmounts.
  const pendingTabFocusRef = useRef<number | null>(null);

  useEffect(() => {
    if (pendingTabFocusRef.current !== activeTabIndex) return;
    pendingTabFocusRef.current = null;
    tabsRef.current?.focusTab(activeTabIndex);
  }, [activeTabIndex]);

  // Opens the tab that edits a docked property: its docked section's tab, else its own `docked-<name>-tab`.
  // Called only from event handlers, so `editorTabs` (computed once the tabs are built, below) is set.
  const handleDockedPropertyEdit = (propertyName: string) => {
    const tabIndex = editorTabs.tabIndexByProperty[propertyName] ?? -1;
    if (tabIndex === -1) return;
    pendingTabFocusRef.current = tabIndex;
    setActiveTabIndex(tabIndex);
  };

  // Force view mode if the user lacks edit permission
  const effectiveViewMode = viewMode || !canEdit;

  // Don't filter out docked properties from regular properties - they should appear in both places
  const allRegularProperties = useMemo(() => schemaType?.properties || [], [schemaType]);

  // Properties the viewer cannot write (write-gated without the grant, or `read_only`), from the
  // already-fetched permission list. They render disabled + locked, and section slot contexts
  // derive their mode from the same set.
  const writeBlockedProps = useMemo(
    () => computeWriteBlockedProps(allRegularProperties, selectedNodePermissionList),
    [allRegularProperties, selectedNodePermissionList]
  );

  // Properties with access.read whose value was stripped by the backend are hidden.
  // Backend removes the key from sphere.properties when the caller lacks the required permission.
  const visibleRegularProperties = useMemo(
    () =>
      allRegularProperties.filter((prop: any) => {
        if (!prop.access?.read) return true;
        return Object.prototype.hasOwnProperty.call(sphere.properties, prop.name);
      }),
    [allRegularProperties, sphere.properties]
  );

  // Section layout for the docked section tabs. Mirrors the molecule's property set and mode so both
  // agree on which section each field is in.
  const sectionLayout = useMemo(() => {
    if (!schemaType) return { inline: [], docked: [], dockedProperties: [], onlyDefault: true } as SectionLayout;
    const editableInSections = effectiveViewMode ? false : editable;
    return resolveSectionLayout({
      sections: schemaType.sections,
      properties: filterHiddenPropertiesExcluding(visibleRegularProperties, HEADER_RENDERED_PROPERTY_NAMES),
      slots: sectionSlots,
      slotContext: { sphere: editedSphere, schemaType, mode: editableInSections ? 'edit' : 'view', editable: !!editableInSections },
      writeBlockedProps,
      aspectComponents: schemaType.aspectComponents,
    });
  }, [schemaType, visibleRegularProperties, sectionSlots, editedSphere, effectiveViewMode, editable, writeBlockedProps, sectionSlotVersion]);

  // Legacy `dock: true` properties get their own tab unless they sit inside a docked section, whose
  // tab already edits them.
  const dockedProperties = useMemo(
    () => (effectiveViewMode ? [] : sectionLayout.dockedProperties),
    [effectiveViewMode, sectionLayout]
  );

  // Fetch relationships for the sphere
  useEffect(() => {
    const fetchSphereRelationships = async () => {
      if (!graphClient || !sphere.id || !effectiveOrganizationId) {
        console.log('🔄 SphereEditor - Skipping relationship fetch: missing graphClient, sphere.id or organizationId');
        return;
      }

      console.log('🔄 SphereEditor - fetchSphereRelationships starting for:', sphere.id);
      setRelationshipsLoading(true);
      setRelationshipsError(null);

      try {
        const result = await getRelationships(graphClient, {
          sphereId: sphere.id,
        });

        if (result.success && result.relationships) {
          console.log('📥 SphereEditor - Relationships fetched:', result.relationships.length);
          
          // Fetch target sphere details for each relationship
          const relationshipsWithSpheres = await Promise.all(
            result.relationships.map(async (rel: any) => {
              console.log(`🌐 SphereEditor - Fetching sphere details for targetId: ${rel.targetId}`);
              try {
                const sphereResult = await getSphereById(graphClient, rel.targetId, true);
                console.log(`📍 SphereEditor - Sphere result for ${rel.targetId}:`, sphereResult);
                if (sphereResult.success) {
                  const enhanced = {
                    ...rel,
                    targetSphere: sphereResult.data.sphere || sphereResult.data
                  };
                  console.log(`✅ SphereEditor - Enhanced relationship:`, enhanced);
                  return enhanced;
                }
              } catch (error) {
                console.warn(`Failed to fetch target sphere ${rel.targetId}:`, error);
              }
              // Return relationship without sphere details if fetch fails
              console.log(`⚠️ SphereEditor - Returning relationship without sphere details:`, rel);
              return rel;
            })
          );
          
          console.log('🎯 SphereEditor - Final relationships with spheres:', relationshipsWithSpheres);
          setAllRelationships(relationshipsWithSpheres);
        } else {
          const errorMessage = result.error || 'Failed to fetch relationships';
          console.error('❌ SphereEditor - Failed to fetch relationships:', errorMessage);
          setRelationshipsError(errorMessage);
          setAllRelationships([]);
        }
      } catch (error: any) {
        console.error('❌ SphereEditor - Relationship fetch error:', error);
        setRelationshipsError(error.message || 'Failed to fetch relationships');
        setAllRelationships([]);
      } finally {
        setRelationshipsLoading(false);
        console.log('✅ SphereEditor - fetchSphereRelationships completed');
      }
    };

    fetchSphereRelationships();
  }, [sphere.id, effectiveOrganizationId, graphClient]);

  useEffect(() => {
    const resourceId = sphere.id;
    if (!isEditing || !resourceId) return;
    const resourceLabel = sphere.label || sphere.properties?.type || 'sphere';
    // An answer that arrives after the editor moved on (another sphere, or a newer check) is dropped.
    let current = true;
    const checkSpherePermissions = async () => {
      try {
        if (!fetchPermissionList) {
          throw new Error('fetchPermissionList is not defined');
        }
        const permissionList = await fetchPermissionList(resourceId, resourceLabel, ['edit', 'view', 'manage_security']);
        if (!current) return;
        setPermissionAnswer({
          sphereId: resourceId,
          allowed: permissionList?.permissions?.edit ?? false,
          list: permissionList || NO_PERMISSIONS,
        });
      } catch (error) {
        if (!current) return;
        console.error('SphereEditor: Permission check failed:', error);
        setPermissionAnswer({ sphereId: resourceId, allowed: false, list: NO_PERMISSIONS });
      }
    };
    void checkSpherePermissions();
    return () => {
      current = false;
    };
  }, [sphere.id, sphere.label, sphere.properties?.type, isEditing, fetchPermissionList]);


  // Prepare perspective fields from required perspectives in the schema
  // The schema can be absent — still loading, or a type the org no longer has —
  // so every read of it is null-safe; the editor renders without perspectives
  // rather than crashing before the schema arrives.
  const requiredPerspectives = schemaType?.perspectives?.filter((persp: any) => persp.required) ?? [];
  // Formik reinitialises when these change, so they come from the sphere and its own type only: a
  // type switch must not reset what the person has entered (RetypeFormValues moves them instead).
  const perspectiveFields = requiredPerspectiveFields(ownSchema, sphere.assignedPerspectives);
  const perspectiveAsSchemaProps = requiredPerspectives.map((persp: any) => perspectiveToSchemaProperty(persp));

  // Use all properties (docked + regular) for validation, but only regular for the main properties tab
  const allProperties = [...(schemaType?.properties || []), ...perspectiveAsSchemaProps];

  // Define initial values for Formik
  const initialValues = {
    properties: {
      ...sphere.properties,
      ...perspectiveFields,
    },
    assignedPerspectives: sphere.assignedPerspectives || [],
  };

  const validationSchema = editorValidationSchema(allProperties);

  const toastRef = React.useRef<Toast>(null);

  const handleSubmit = async (values: typeof initialValues, actions: any) => {
    try {
      // AC5: Filter out is_private from payload if user lacks manage_security permission
      const hasManageSecurityPermission = selectedNodePermissionList?.permissions?.manage_security ?? false;
      const filteredProperties = { ...values.properties };

      if (!hasManageSecurityPermission && 'is_private' in filteredProperties) {
        delete filteredProperties.is_private;
        console.log('[SphereEditor] Filtered is_private from payload (user lacks manage_security)');
      }

      // A type switch drops the fields the new type does not carry; they must not return from the
      // sphere's original properties.
      const baseProperties = chosenType
        ? Object.fromEntries(Object.entries(editedSphere.properties).filter(([name]) => name in values.properties))
        : editedSphere.properties;

      const updatedSphere: Sphere = {
        ...editedSphere,
        properties: { ...baseProperties, ...filteredProperties },
        assignedPerspectives: values.assignedPerspectives,
      };

      console.log('[SphereEditor] Submitting updated sphere:', updatedSphere);
      console.log('[SphereEditor] Assigned perspectives detail:', {
        count: values.assignedPerspectives?.length || 0,
        perspectives: values.assignedPerspectives?.map(ap => ({
          systemName: ap.perspectiveProperties.system_name,
          assignedCount: ap.assigned.length,
          assignedIds: ap.assigned.map((a: any) => a.sphere.id),
          edgeIds: ap.assigned.map((a: any) => a.edge.edgeId)
        }))
      });

      // Step 1: Update sphere properties using the existing endpoint
      const ack = await onUpdate(updatedSphere);
      console.log('[SphereEditor] Update acknowledgment:', ack);

      if (!ack.success) {
        toastRef.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: 'message' in ack ? ack.message : 'Failed to update Sphere properties.',
          life: 3000,
        });
        actions.setSubmitting(false);
        return;
      }

      // Trigger refresh of expected aspects after successful save
      setSavedRevision(prev => prev + 1);
      invalidateSectionSlots(sphere.id);

      // Step 2: Handle perspective assignments using dedicated endpoints
      if (graphClient) {
        console.log('[SphereEditor] Processing perspective assignments...');

        const assignmentErrors: string[] = [];
        const initialAssignments = sphere.assignedPerspectives || [];
        const newAssignments = values.assignedPerspectives || [];

        for (const newPerspective of newAssignments) {
          const perspectiveSystemName = newPerspective.perspectiveProperties.system_name;
          const newSubjectIds = newPerspective.assigned.map((a: any) => a.sphere.id);

          const initialPerspective = initialAssignments.find(
            p => p.perspectiveProperties.system_name === perspectiveSystemName
          );
          const initialSubjectIds = initialPerspective?.assigned?.map((a: any) => a.sphere.id) || [];

          console.log(`[SphereEditor] Processing ${perspectiveSystemName}:`, {
            initial: initialSubjectIds,
            new: newSubjectIds
          });

          const subjectsToAdd = newSubjectIds.filter(id => !initialSubjectIds.includes(id));
          const subjectsToRemove = initialSubjectIds.filter(id => !newSubjectIds.includes(id));

          for (const subjectId of subjectsToAdd) {
            try {
              console.log(`[SphereEditor] Assigning ${subjectId} to ${perspectiveSystemName}`);
              const resp = await graphClient.post<{ success: boolean; message?: string }>(
                `/spheres/${encodeURIComponent(sphere.id)}/perspectives/assign-subject`,
                { perspectiveSystemName, subjectId }
              );
              if (!resp.success) {
                assignmentErrors.push(`Failed to assign ${subjectId} to ${perspectiveSystemName}: ${resp.message}`);
              }
            } catch (error: any) {
              console.error(`[SphereEditor] Error assigning ${subjectId} to ${perspectiveSystemName}:`, error);
              assignmentErrors.push(`Error assigning ${subjectId} to ${perspectiveSystemName}: ${error.message}`);
            }
          }

          for (const subjectId of subjectsToRemove) {
            try {
              const perspectiveId = newPerspective.perspectiveProperties.id;
              console.log(`[SphereEditor] Unassigning ${subjectId} from ${perspectiveSystemName} (perspectiveId: ${perspectiveId})`);
              const resp = await graphClient.delete<{ success: boolean; message?: string }>(
                `/perspectives/${encodeURIComponent(perspectiveId)}/subjects/${encodeURIComponent(subjectId)}`
              );
              if (!resp.success) {
                assignmentErrors.push(`Failed to unassign ${subjectId} from ${perspectiveSystemName}: ${resp.message}`);
              }
            } catch (error: any) {
              console.error(`[SphereEditor] Error unassigning ${subjectId} from ${perspectiveSystemName}:`, error);
              assignmentErrors.push(`Error unassigning ${subjectId} from ${perspectiveSystemName}: ${error.message}`);
            }
          }
        }

        for (const initialPerspective of initialAssignments) {
          const perspectiveSystemName = initialPerspective.perspectiveProperties.system_name;
          const newPerspective = newAssignments.find(
            p => p.perspectiveProperties.system_name === perspectiveSystemName
          );

          if (!newPerspective || newPerspective.assigned.length === 0) {
            const initialSubjectIds = initialPerspective.assigned.map((a: any) => a.sphere.id);
            const perspectiveId = initialPerspective.perspectiveProperties.id;

            for (const subjectId of initialSubjectIds) {
              try {
                console.log(`[SphereEditor] Unassigning ${subjectId} from ${perspectiveSystemName} (perspectiveId: ${perspectiveId})`);
                const resp = await graphClient.delete<{ success: boolean; message?: string }>(
                  `/perspectives/${encodeURIComponent(perspectiveId)}/subjects/${encodeURIComponent(subjectId)}`
                );
                if (!resp.success) {
                  assignmentErrors.push(`Failed to unassign ${subjectId} from ${perspectiveSystemName}: ${resp.message}`);
                }
              } catch (error: any) {
                console.error(`[SphereEditor] Error unassigning ${subjectId} from ${perspectiveSystemName}:`, error);
                assignmentErrors.push(`Error unassigning ${subjectId} from ${perspectiveSystemName}: ${error.message}`);
              }
            }
          }
        }

        if (assignmentErrors.length > 0) {
          console.error('[SphereEditor] Perspective assignment errors:', assignmentErrors);
          toastRef.current?.show({
            severity: 'warn',
            summary: 'Partial Success',
            detail: `Properties updated, but some perspective assignments failed: ${assignmentErrors[0]}`,
            life: 5000,
          });
        } else {
          console.log('[SphereEditor] All perspective assignments processed successfully');
          toastRef.current?.show({
            severity: 'success',
            summary: 'Success',
            detail: 'Sphere properties and perspective assignments updated successfully.',
            life: 3000,
          });
        }
      } else {
        toastRef.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: 'Sphere properties updated successfully.',
          life: 3000,
        });
      }

      actions.setSubmitting(false);
    } catch (error) {
      console.error('Error during submission:', error);
      toastRef.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: (error as Error)?.message ?? 'Failed to update Sphere properties.',
        life: 3000,
      });
      actions.setSubmitting(false);
    }
  };

  // Component for editing sphere properties (now uses only regular properties)
  // SpherePropertiesEditor is hoisted to module scope (above SphereEditorOrganism)
  // so its component identity is stable across parent re-renders. See the
  // interface comment for why.

  // --- Responsible Perspectives Fetching ---
  const fetchResponsiblePerspectives = useCallback(
    async (sphereId: string) => {
      if (!graphClient) return [];
      try {
        const data = await graphClient.get<any[]>(`/spheres/${encodeURIComponent(sphereId)}/perspectives`);
        const filtered = data.filter((p) => p.properties?.system_name !== 'center_parent' && p.properties?.system_name !== 'center_associated_identity');
        setSelectedResponsiblePerspective(filtered);
        return filtered;
      } catch (err) {
        console.error('Error fetching responsible perspectives:', err);
        return [];
      }
    },
    [graphClient]
  );

  // --- Perspective Assignment Section ---
  // Local state for perspective dropdowns and assignments
  const [updatedSelectedOptions, setUpdatedSelectedOptions] = useState<any[]>([]);
  const [selectedResponsiblePerspective, setSelectedResponsiblePerspective] = useState<any[]>([]);

  const columns = useMemo(
    () => [
      { field: 'subjects', header: 'Assignee' },
      { field: 'perspectives', header: 'Perspectives' },
      // { field: 'description', header: 'Description' }
    ],
    []
  );

  const getResponsiblePerspectivesOptions = useCallback((responsiblePerspectives: any[], updatedOptions: any[]) => {
    const selectedValues = new Set(updatedOptions.map((option) => option.value));
    return responsiblePerspectives.map((persp: any) => ({
      label: persp.properties?.name || persp.label || 'Unknown Perspective',
      value: persp.id || persp.value || persp.label,
      selected: selectedValues.has(persp.id || persp.value),
      description: persp.description || persp.properties?.description,
      systemName: persp.properties?.system_name ?? persp.systemName,
    }));
  }, []);

  const responsiblePerspectiveOptions = useMemo(() => {
    return getResponsiblePerspectivesOptions(selectedResponsiblePerspective, updatedSelectedOptions);
  }, [getResponsiblePerspectivesOptions, selectedResponsiblePerspective, updatedSelectedOptions]);

  const handleAssignPerspective = useCallback(
    async (selectedOption: any) => {
      if (!graphClient) return;
      const subjectId = selectedParticipant.id;
      try {
        if (selectedOption.selected) {
          // Assign the subject to the perspective
          const resp = await graphClient.post<{
            success: boolean;
            message?: string;
          }>(`/spheres/${encodeURIComponent(sphere.id)}/perspectives/${encodeURIComponent(selectedOption.systemName)}/assign`, { subjectId });
          if (!resp.success) {
            throw new Error(resp.message);
          }
          console.log(`Assigned subject ${subjectId} to perspective ${selectedOption.systemName}`);
        } else {
          // Unassign the subject from the perspective
          const resp = await graphClient.post<{
            success: boolean;
            message?: string;
          }>(`/spheres/${encodeURIComponent(sphere.id)}/perspectives/remove-subject`, {
            perspectiveSystemName: selectedOption.systemName,
            subjectId
          });
          if (!resp.success) {
            throw new Error(resp.message);
          }
          console.log(`Unassigned subject ${subjectId} from perspective ${selectedOption.systemName}`);
        }
      } catch (error) {
        console.error('Error assigning subject:', error);
      }
    },
    [selectedParticipant, sphere.id, graphClient]
  );

  const handleResponsiblePerspectiveChange = useCallback((e: { value: any[] }) => {
    setUpdatedSelectedOptions(e.value);
  }, []);

  // When the Perspectives tab becomes active, fetch responsible perspectives.
  useEffect(() => {
    if (sphere.id) {
      fetchResponsiblePerspectives(sphere.id);
    }
  }, [sphere.id, fetchResponsiblePerspectives]);

  // Set initial tab index when initialTab prop is provided
  // This is handled inside the render because we need access to allTabs

  // Period selector handlers
  const handleShowPeriodSelector = useCallback(() => {
    const periodRels = allRelationships.filter(
      (rel: any) => rel.relationshipType === 'applies_to_period' && rel.active && rel.targetSphere
    );

    setPeriodSelectorValue(periodRels.map((rel: any) => rel.targetSphere));
    setPeriodSelectorExistingRels(
      periodRels.map((rel: any) => ({
        periodSphereId: rel.targetId,
        relationshipId: rel.id,
      }))
    );
    setShowPeriodSelector(true);
  }, [allRelationships]);

  const handlePeriodSelectorApply = useCallback(() => {
    setShowPeriodSelector(false);

    if (!graphClient || !sphere.id || !effectiveOrganizationId) return;

    // Refresh relationships after the Organism has handled materialization + relationship changes
    setTimeout(async () => {
      try {
        const result = await getRelationships(graphClient, {
          sphereId: sphere.id,
        });

        if (result.success && result.relationships) {
          const relationshipsWithSpheres = await Promise.all(
            result.relationships.map(async (rel: any) => {
              try {
                const sphereResult = await getSphereById(graphClient, rel.targetId, true);
                if (sphereResult.success) {
                  return { ...rel, targetSphere: sphereResult.data.sphere || sphereResult.data };
                }
              } catch {
                // target sphere lookup failed — keep partial relationship
              }
              return rel;
            })
          );
          setAllRelationships(relationshipsWithSpheres);
        }
      } catch {
        // relationship refresh failed — user can reload to see updates
      }
    }, 500);
  }, [sphere.id, effectiveOrganizationId, graphClient]);

  const handlePeriodSelectorCancel = useCallback(() => {
    setShowPeriodSelector(false);
    setPeriodSelectorValue([]);
    setPeriodSelectorExistingRels([]);
  }, []);

  // Helper function to check if perspective assignments have changed
  const hasPerspectiveAssignmentsChanged = useCallback((currentAssignments: any[]) => {
    const initial = initialPerspectiveAssignmentsRef.current;

    // Compare each perspective's assigned subjects
    for (const currentPerspective of currentAssignments) {
      const perspectiveSystemName = currentPerspective.perspectiveProperties?.system_name;
      const currentSubjectIds = (currentPerspective.assigned || []).map((a: any) => a.sphere?.id).sort();

      const initialPerspective = initial.find(
        (p: any) => p.perspectiveProperties?.system_name === perspectiveSystemName
      );
      const initialSubjectIds = (initialPerspective?.assigned || []).map((a: any) => a.sphere?.id).sort();

      // Check if the subject lists are different
      if (currentSubjectIds.length !== initialSubjectIds.length) {
        return true;
      }

      for (let i = 0; i < currentSubjectIds.length; i++) {
        if (currentSubjectIds[i] !== initialSubjectIds[i]) {
          return true;
        }
      }
    }

    // Also check if any initial perspectives were completely removed
    for (const initialPerspective of initial) {
      const perspectiveSystemName = initialPerspective.perspectiveProperties?.system_name;
      const currentPerspective = currentAssignments.find(
        (p: any) => p.perspectiveProperties?.system_name === perspectiveSystemName
      );

      if (!currentPerspective && (initialPerspective.assigned || []).length > 0) {
        return true;
      }
    }

    return false;
  }, []);

  // Handle close button with unsaved changes check
  const handleClose = useCallback((currentAssignments?: any[]) => {
    // Prevent multiple dialogs from opening
    if (isConfirmDialogOpenRef.current) {
      return;
    }

    const hasUnsavedPerspectiveChanges = currentAssignments
      ? hasPerspectiveAssignmentsChanged(currentAssignments)
      : false;
    const hasAnyUnsavedChanges = hasUnsavedFactorChanges || hasUnsavedPerspectiveChanges;

    if (hasAnyUnsavedChanges) {
      // Build a message that describes what has unsaved changes
      const changeTypes: string[] = [];
      if (hasUnsavedPerspectiveChanges) {
        changeTypes.push(t('sphereEditor.perspectiveAssignments', 'perspective assignments'));
      }
      if (hasUnsavedFactorChanges) {
        changeTypes.push(t('sphereEditor.statusFactors', 'status factors'));
      }

      const changeDescription = changeTypes.join(` ${t('common.and', 'and')} `);

      isConfirmDialogOpenRef.current = true;
      confirmDialogAtom({
        message: t('sphereEditor.confirmClose.message',
          `You have unsaved changes to ${changeDescription}. Are you sure you want to close?`,
          { changeDescription }
        ),
        header: t('sphereEditor.confirmClose.header', 'Unsaved Changes'),
        icon: 'pi pi-exclamation-triangle',
        variant: 'danger',
        acceptLabel: t('sphereEditor.confirmClose.discard', 'Discard Changes'),
        rejectLabel: t('common.cancel', 'Cancel'),
        accept: () => {
          isConfirmDialogOpenRef.current = false;
          onClose?.();
        },
        reject: () => {
          isConfirmDialogOpenRef.current = false;
        },
        onHide: () => {
          isConfirmDialogOpenRef.current = false;
        },
      });
    } else {
      onClose?.();
    }
  }, [hasUnsavedFactorChanges, hasPerspectiveAssignmentsChanged, onClose, t]);

  // Handle save button with unsaved factor changes check
  const handleSaveClick = useCallback((formikHandleSubmit: () => void) => {
    if (hasUnsavedFactorChanges) {
      confirmDialogAtom({
        message: t('statusFactors.confirmSave.message', 'You have unsaved changes in the Status Factors tab. These changes will be lost if you save now. Do you want to continue?'),
        header: t('statusFactors.confirmSave.header', 'Unsaved Status Factor Changes'),
        icon: 'pi pi-exclamation-triangle',
        variant: 'warning',
        acceptLabel: t('statusFactors.confirmSave.continue', 'Save Anyway'),
        rejectLabel: t('common.cancel', 'Cancel'),
        accept: () => {
          formikHandleSubmit();
        },
      });
    } else {
      formikHandleSubmit();
    }
  }, [hasUnsavedFactorChanges, t]);

  // Rolls-up-into indicator (Shared Measures): shown whenever this sphere has a
  // currently-resolved active outbound aggregate link. Aggregates chain, so a
  // mid-level aggregate rolls up too — gate on the link, not the measureRole.
  const measuresIntoChip =
    sphere.measuresInto
      ? (
        <ChipAtom
          label={t('measureMembers.rollupIndicator.label', { aggregateName: sphere.measuresInto.sphereName })}
          icon="lucide:corner-right-up"
          className="sphere-editor__measures-into-chip"
        />
      )
      : null;

  // Helper function to render the main content based on current state
  const renderMainContent = () => {
    if (schemaType) {
      return (
        <>
          {measuresIntoChip}
          <SpherePropertiesEditor
            sphere={editedSphere}
            schemaType={schemaType}
            editable={editable}
            parentSphereType={parentSphereType}
            isEditing={isEditing}
            perspectiveAssignments={initialValues.assignedPerspectives}
            setFieldValue={() => {
              // intentionally empty, required by interface
            }}
            onDockedPropertyEdit={handleDockedPropertyEdit}
            organizationId={organizationId}
            onNavigateToArc={(arcId) => {
              pendingArcIdRef.current = arcId;
              setPendingArcId(arcId);
            }}
            allRelationships={allRelationships}
            setAllRelationships={setAllRelationships}
            relationshipsLoading={relationshipsLoading}
            relationshipsError={relationshipsError}
            effectiveViewMode={effectiveViewMode}
            effectiveOrganizationId={effectiveOrganizationId}
            graphClient={graphClient}
            selectedNodePermissionList={selectedNodePermissionList}
            allRegularProperties={visibleRegularProperties}
            handleShowPeriodSelector={handleShowPeriodSelector}
            writeBlockedProps={writeBlockedProps}
            sectionSlots={sectionSlots}
            onTypeChange={handleTypeChange}
            savedRevision={savedRevision}
            onOpenSectionTab={handleOpenSectionTab}
            onSphereChanged={onSphereChanged}
          />
        </>
      );
    }

    if (schemaTypeStatus === 'failed') {
      return (
        <EmptyStateAtom
          icon="pi-exclamation-triangle"
          title={t('sphere.schemaLoadError.title')}
          description={t('sphere.schemaLoadError.retryDescription')}
          data-testid="sphere-schema-load-error"
        >
          <ButtonAtom variant="secondary" label={t('sphere.schemaLoadError.retry')} onClick={retrySchemaType} />
        </EmptyStateAtom>
      );
    }

    if (schemaTypeStatus === 'notFound' || !typeName) {
      return (
        <EmptyStateAtom
          icon="pi-question-circle"
          title={t('sphere.schemaNotFound.title')}
          description={t('sphere.schemaNotFound.description')}
          data-testid="sphere-schema-not-found"
        />
      );
    }

    return (
      <output className="sphere-editor__schema-loading" aria-busy="true" aria-label={t('sphere.schemaLoading')} data-testid="sphere-schema-loading">
        <SkeletonAtom height="2.5rem" count={4} gap="var(--spacing-md)" />
      </output>
    );
  };

  // Every tab below is independent of Formik's per-field state, so each is computed once per
  // render here rather than inside the Formik render-prop (previously recomputed on every
  // keystroke, and — with the array's `...(tab ? [tab] : [])` spread — Cognitive Complexity 30
  // in one function; extracting keeps every tab-builder's own branching out of the caller).
  const arcsTab = buildArcsTab({ sphere, effectiveViewMode, effectiveOrganizationId, apiClients, canEdit, onStatusFactorsChanged, pendingArcIdRef, initialArcId, t });
  const purposeTab = buildPurposeTab({ sphere, graphClient, canEdit, effectiveViewMode, effectiveOrganizationId, t });
  const perspectiveTab = buildPerspectiveTab({
    sphere,
    effectiveViewMode,
    t,
    columns,
    responsiblePerspectiveOptions,
    handleResponsiblePerspectiveChange,
    setSelectedResponsiblePerspective,
    updatedSelectedOptions,
    selectedNodePermissionList,
    selectedPerspectiveSystemName,
    setSelectedPerspectiveSystemName,
    onParticipantSelect: setSelectedParticipant,
    handleAssignPerspective,
    selectedParticipant,
    onPerspectiveSelect: setSelectedPerspective,
    selectedPerspective,
  });
  const statusFactorsTab = buildStatusFactorsTab({
    sphere,
    effectiveViewMode,
    isMobile,
    t,
    apiClients,
    effectiveOrganizationId,
    canEdit,
    setHasUnsavedFactorChanges,
    onStatusFactorsChanged,
  });
  const approachesTab = buildApproachesTab({ sphere, effectiveViewMode, t, apiClients, effectiveOrganizationId, canEdit });
  const channelsTab = buildChannelsTab({ sphere, effectiveViewMode, t, apiClients, effectiveOrganizationId, canEdit });
  const measureAggregateTab = buildMeasureAggregateTab({ sphere, canEdit, effectiveViewMode, t });

  // Properties, Arcs, Purpose, Perspective Assignments, Status Factors, Approaches, Channels, Measures;
  // the docked section tabs and then the legacy docked tabs follow them. The Properties tab's content
  // needs handleOpenSectionTab, which needs the tab index, so the tab index is built from ids first.
  const PROPERTIES_TAB_ID = 'properties-tab';
  const tabsAfterProperties = [
    arcsTab,
    purposeTab,
    perspectiveTab,
    statusFactorsTab,
    approachesTab,
    channelsTab,
    measureAggregateTab,
  ].filter(Boolean) as Array<{ id: string; header: string; content: React.ReactElement }>;

  const editorTabs = useEditorTabIndex({
    leadingTabIds: [PROPERTIES_TAB_ID, ...tabsAfterProperties.map((tab) => tab.id)],
    dockedGroups: sectionLayout.docked,
    dockedProperties,
    properties: allRegularProperties,
  });

  // Open/focus a section tab when expected aspect's Show is clicked
  const handleOpenSectionTab = useCallback((sectionId: string) => {
    const tabId = sectionTabId(sectionId);
    const tabIndex = editorTabs.tabIds.indexOf(tabId);
    if (tabIndex !== -1) {
      pendingTabFocusRef.current = tabIndex;
      setActiveTabIndex(tabIndex);
    }
  }, [editorTabs.tabIds]);

  const leadingTabs = [
    { id: PROPERTIES_TAB_ID, header: t('sphere.editor.tabs.properties'), content: renderMainContent() },
    ...tabsAfterProperties,
  ];

  // Early return if API clients are not available (must be after all hooks)
  if (!apiClients) {
    return null;
  }

  const sectionTitle = (group: SectionLayoutGroup) =>
    group.section ? getTranslation(i18n, group.section.label_key, group.section.label ?? group.key) : t('sphere.sections.details');

  // A docked section edits its fields in edit mode and shows them in view mode; legacy docked
  // properties inside it render their full editor, since no separate tab exists for them.
  const renderDockedSectionBody = (group: SectionLayoutGroup) =>
    schemaType && (
    <SectionGroupBodyMolecule
      properties={group.properties}
      schemaType={schemaType}
      editable={effectiveViewMode ? false : editable}
      writeBlockedProps={writeBlockedProps}
      slot={group.slot}
      slotContext={group.slotContext}
      renderDockedInline
    />
    );

  return (
    <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit} enableReinitialize>
      {({ handleSubmit, isSubmitting, setFieldValue, values, errors, touched, submitCount }) => {
        // Create docked property tabs dynamically — always resolved in `edit` mode (docked tabs
        // are the dedicated editing surface for the property, EAINT-855 dockedTabsRenderThroughUnifiedResolver).
        // Every property now resolves to SOME component (the chain's terminal step always
        // produces one), so — unlike the pre-Phase-4 code — there is no "no edit component
        // configured" fallback state left to render.
        const dockedTabs = dockedProperties.map((prop: any) => {
          // ADR-022 write gate. Docked tabs are only mounted when the user may edit the sphere, but
          // sphere-level edit does not imply permission to write THIS property — and since Phase 4
          // every docked property resolves to a real editable control (previously one with no
          // `editComponent` rendered an inert "no edit component configured" message), so without
          // this a write-blocked docked property would offer an affordance whose save 403s and
          // discards the entire sphere update.
          const isWriteBlocked = writeBlockedProps.has(prop.name);
          const dockedMode: RenderMode = isWriteBlocked ? 'view' : 'edit';
          const resolved = resolveRenderer(prop, dockedMode);
          const isMultiField = isMultiFieldLegacy(resolved);
          const propertyValue = values.properties?.[prop.name];
          const rendererValue = isMultiField ? values : propertyValue;

          return {
            id: dockedPropertyTabId(prop.name),
            header: prop.label || prop.name,
            content: (
              <div className="docked-property-tab-content">
                <UnifiedPropertyRendererMolecule
                  property={prop}
                  mode={dockedMode}
                  value={rendererValue}
                  onChange={isWriteBlocked ? undefined : (newValue: unknown) => {
                    // Multi-field: fan out to each mapped path only (matches the pre-Phase-4
                    // docked-tab handler exactly — unlike AdditionalPropertiesGrid's
                    // renderCustomComponent, this call site never ALSO writes the whole payload
                    // to `properties.<prop.name>` in the multi-field case).
                    if (isMultiFieldChangeMapper(resolved)) {
                      fanOutMultiFieldChange(newValue, resolved.legacyShim?.changeMapper, values, setFieldValue);
                    } else {
                      // Covers both the plain single-value shim AND a multiField `valueAccessor`
                      // with no `changeMapper` — see `AdditionalPropertiesGrid`'s identical branch
                      // for why the write decision can't reuse `isMultiField` (EAINT-855 review
                      // pass 2, L-10).
                      setIfChanged(`properties.${prop.name}`, newValue, propertyValue, setFieldValue);
                    }
                  }}
                  // Allow docked components (like SchedulesEditorMolecule) to switch back to
                  // properties tab — meaningful only to the legacy-shim path, harmless extra
                  // prop otherwise.
                  extraProps={{ editable: editable && !isWriteBlocked, onApplyComplete: () => setActiveTabIndex(0) }}
                />
              </div>
            ),
          };
        });

        const sectionTabs = sectionLayout.docked.map((group) => ({
          id: sectionTabId(group.key),
          header: sectionTitle(group),
          content: (
            <div className="docked-section-tab-content">
              {renderDockedSectionBody(group)}
            </div>
          ),
        }));

        // Same order as `editorTabs.tabIds`. Attachments render inline in SphereProperties (see
        // AttachmentsGridMolecule). The leading tabs are computed above (they don't depend on Formik state).
        const allTabs = [...leadingTabs, ...sectionTabs, ...dockedTabs];

        // Apply initial tab index from initialTab ID (only on first render)
        if (!initialTabAppliedRef.current && initialTab) {
          const computedInitialTabIndex = allTabs.findIndex((tab) => tab.id === initialTab);
          if (computedInitialTabIndex >= 0) {
            setActiveTabIndex(computedInitialTabIndex);
          }
          initialTabAppliedRef.current = true;
        }

        // Navigate to Arcs tab when pendingArcId is set from Properties tab
        if (pendingArcId) {
          const arcsTabIndex = allTabs.findIndex((tab) => tab.id === 'arcs-tab');
          if (arcsTabIndex >= 0) {
            setActiveTabIndex(arcsTabIndex);
          }
          // Clear state but keep ref so SphereArcsTab can read it for initial selection
          setPendingArcId(null);
        }

        const dockedGroupHasErrors = (group: SectionLayoutGroup) =>
          group.allProperties.some((prop) => {
            const error = (errors.properties as Record<string, unknown> | undefined)?.[prop.name];
            const isTouched = (touched.properties as Record<string, unknown> | undefined)?.[prop.name];
            return !!error && (!!isTouched || submitCount > 0);
          });

        return (
          <Form onSubmit={handleSubmit} className="sphere-editor-form">
            <Toast ref={toastRef} />
            <ConfirmDialogAtom />
            <Dialog
              visible={showPeriodSelector}
              onHide={handlePeriodSelectorCancel}
              header={t('periodSelector.periodselectorheader')}
              modal
              className="period-selector-dialog"
              style={{ width: '90vw', maxWidth: '1100px', height: '75vh' }}
              contentStyle={{ padding: 0, height: '100%', overflow: 'hidden' }}
              dismissableMask
            >
              <PeriodSelectorOrganism
                sphereId={sphere.id || ''}
                organizationId={effectiveOrganizationId || ''}
                value={periodSelectorValue}
                existingRelationships={periodSelectorExistingRels}
                onApply={handlePeriodSelectorApply}
                onCancel={handlePeriodSelectorCancel}
              />
            </Dialog>
            <FormikDebugger />
            <RetypeFormValues schema={schemaType} />
            <FirstInvalidTabActivator
              propertyOrder={editorTabs.propertyOrder}
              tabIndexByProperty={editorTabs.tabIndexByProperty}
              onActivate={setActiveTabIndex}
            />
            {showTabs ? (
              <CustomTabs
                ref={tabsRef}
                tabs={allTabs}
                tabViewClassName="sphere-editor-tabs-view"
                activeIndex={activeTabIndex}
                onTabChange={setActiveTabIndex}
              />
            ) : (
              <>
              <SpherePropertiesEditor
                sphere={editedSphere}
                schemaType={schemaType}
                editable={editable}
                parentSphereType={parentSphereType}
                isEditing={isEditing}
                perspectiveAssignments={initialValues.assignedPerspectives}
                setFieldValue={setFieldValue}
                onDockedPropertyEdit={handleDockedPropertyEdit}
                organizationId={organizationId}
                onNavigateToArc={(arcId) => {
                  pendingArcIdRef.current = arcId;
                  setPendingArcId(arcId);
                }}
                allRelationships={allRelationships}
                setAllRelationships={setAllRelationships}
                relationshipsLoading={relationshipsLoading}
                relationshipsError={relationshipsError}
                effectiveViewMode={effectiveViewMode}
                effectiveOrganizationId={effectiveOrganizationId}
                graphClient={graphClient}
                selectedNodePermissionList={selectedNodePermissionList}
                allRegularProperties={visibleRegularProperties}
                handleShowPeriodSelector={handleShowPeriodSelector}
                writeBlockedProps={writeBlockedProps}
                sectionSlots={sectionSlots}
                onTypeChange={handleTypeChange}
                savedRevision={savedRevision}
                onOpenSectionTab={handleOpenSectionTab}
                onSphereChanged={onSphereChanged}
              />
              {sectionLayout.docked.map((group) => (
                <CollapsibleSectionAtom
                  key={group.key}
                  title={sectionTitle(group)}
                  subtitle={
                  group.section?.description
                    ? getTranslation(i18n, group.section.description_key, group.section.description)
                    : undefined
                }
                  hasErrors={dockedGroupHasErrors(group)}
                  errorMessage={t('sphere.sections.hasErrors')}
                  showErrorMessage
                  anchorId={`sphere-section-${group.key}`}
                  data-testid={`sphere-section-${group.key}`}
                >
                  {renderDockedSectionBody(group)}
                </CollapsibleSectionAtom>
              ))}
              </>
            )}
            <div className="form-actions">
              {!viewMode && (
                <div className="save-button-container">
                  <button
                    type="button"
                    disabled={isSphereSubmitting || isSubmitting}
                    className={`Save-button ${isSphereSubmitting || isSubmitting ? 'disabled' : ''}`}
                    onClick={() => handleSaveClick(handleSubmit)}
                  >
                    {isSphereSubmitting || isSubmitting ? (
                      <>
                        <span className="spinner"></span> Saving...
                      </>
                    ) : (
                      'Save'
                    )}
                  </button>
                </div>
              )}
              <div className="close-button-container">
                <button
                  type="button"
                  disabled={isSphereSubmitting || isSubmitting}
                  className="Close-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClose(values.assignedPerspectives);
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

export default SphereEditorOrganism;
